import { CandidatoService } from '@/modules/business/candidato/candidato.service';
import { ReadCandidatoDto } from '@/api.types';
import { ProcessoSeletivoService, ReadProcessoSeletivoDto } from './processo-seletivo.service';
import { CursoService } from '@/modules/structure/curso/curso.service';
import { ReadCursoDto, ReadInstituicaoEnsinoDto } from '@/api.types';
import { InstituicaoEnsinoService } from '@/modules/structure/instituicoes/instituicao.service';
import { SearchDto } from '@/shared/types/search.dto';
import Links from '@/shared/constants/navegacao';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

const candidatoService = new CandidatoService();
const processoSeletivoService = new ProcessoSeletivoService();
const cursoService = new CursoService();
const instituicaoEnsinoService = new InstituicaoEnsinoService();

const CHART_COLORS = [
  '#0d6efd',
  '#198754',
  '#ffc107',
  '#dc3545',
  '#6f42c1',
  '#fd7e14',
  '#20c997',
  '#6c757d',
  '#0dcaf0',
  '#d63384',
];

const RACAS_LABELS: Record<string, string> = {
  branca: 'Branca',
  preta: 'Preta',
  parda: 'Parda',
  amarela: 'Amarela',
  indigena: 'Indígena',
};

function normalizarMunicipio(s: string | undefined | null): string {
  return (s ?? '').trim().toLowerCase();
}

async function carregarTodosCandidatosDoProcesso(processoSeletivoId: number): Promise<ReadCandidatoDto[]> {
  const todos: ReadCandidatoDto[] = [];
  const pageSize = 500;
  let skip = 0;
  let total = Infinity;

  while (skip < total) {
    const [rows, count] = await candidatoService.findAll({ skip, take: pageSize }, processoSeletivoId);
    total = count;
    todos.push(...rows);
    if (rows.length === 0) break;
    skip += rows.length;
  }
  return todos;
}

function contarPor<T extends ReadCandidatoDto>(
  rows: T[],
  rotulo: (r: T) => string,
): { name: string; value: number }[] {
  const map = new Map<string, number>();
  for (const r of rows) {
    const k = rotulo(r);
    map.set(k, (map.get(k) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

/** Legenda na ordem do array `data` (maior quantidade primeiro — igual ao gráfico e ao contarPor). */
function LegendaVerticalEmpilhada({ data }: { data: { name: string; value: number }[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!data.length) return null;
  return (
    <ul className="list-unstyled mb-0 small text-start" style={{ maxWidth: '100%' }}>
      {data.map((d, i) => {
        const pct = total > 0 ? ((d.value / total) * 100).toFixed(0) : '0';
        return (
          <li key={`${d.name}-${i}`} className="d-flex align-items-start gap-2 mb-2">
            <span
              className="rounded flex-shrink-0 mt-1"
              style={{ width: 10, height: 10, background: CHART_COLORS[i % CHART_COLORS.length] }}
              aria-hidden
            />
            <span style={{ wordBreak: 'break-word', lineHeight: 1.35 }}>
              {d.name} — {d.value} ({pct}%)
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function PieCard({
  titulo,
  data,
  vazio,
  legendaVertical,
}: {
  titulo: string;
  data: { name: string; value: number }[];
  vazio?: string;
  /** Sem rótulos na fatia; nomes empilhados na legenda (útil para textos longos). */
  legendaVertical?: boolean;
}) {
  if (!data.length) {
    return (
      <div className="card h-100">
        <div className="card-header">
          <h6 className="mb-0">{titulo}</h6>
        </div>
        <div className="card-body d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 280 }}>
          {vazio ?? 'Sem dados'}
        </div>
      </div>
    );
  }

  const alturaGrafico = legendaVertical
    ? Math.max(300, Math.min(560, 80 + data.length * 32))
    : 280;

  return (
    <div className="card h-100">
      <div className="card-header">
        <h6 className="mb-0">{titulo}</h6>
      </div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={alturaGrafico}>
          <PieChart margin={legendaVertical ? { top: 8, right: 8, bottom: 8, left: 8 } : undefined}>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx={legendaVertical ? '38%' : '50%'}
              cy="50%"
              outerRadius={legendaVertical ? Math.min(78, alturaGrafico / 2 - 24) : 90}
              label={legendaVertical ? false : ({ name, percent }) =>
                `${name ?? ''}: ${percent != null ? (percent * 100).toFixed(0) : 0}%`
              }
            >
              {data.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [value ?? 0, 'Quantidade']} />
            {legendaVertical ? (
              <Legend
                layout="vertical"
                verticalAlign="middle"
                align="right"
                wrapperStyle={{ width: '58%', paddingLeft: 4, fontSize: 12 }}
                content={() => <LegendaVerticalEmpilhada data={data} />}
              />
            ) : (
              <Legend />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function ProcessoSeletivoAnalisesPage() {
  const { setActions } = useBreadcrumbActions();

  const [processos, setProcessos] = useState<ReadProcessoSeletivoDto[]>([]);
  const [cursos, setCursos] = useState<ReadCursoDto[]>([]);

  const [processoSeletivoId, setProcessoSeletivoId] = useState<number | ''>('');
  /** Valor exibido no select = texto do município (como nas vagas do processo). */
  const [filtroMunicipio, setFiltroMunicipio] = useState('');
  const [filtroCursoId, setFiltroCursoId] = useState<number | ''>('');

  const [candidatos, setCandidatos] = useState<ReadCandidatoDto[]>([]);
  const [instituicoesEnsino, setInstituicoesEnsino] = useState<ReadInstituicaoEnsinoDto[]>([]);
  const [carregandoCandidatos, setCarregandoCandidatos] = useState(false);
  const [carregandoListas, setCarregandoListas] = useState(true);

  const razaoSocialPorInstituicaoId = useMemo(() => {
    const m = new Map<number, string>();
    for (const i of instituicoesEnsino) {
      m.set(i.id, i.razao_social?.trim() || '');
    }
    return m;
  }, [instituicoesEnsino]);

  const processoSelecionado = useMemo(
    () => (processoSeletivoId === '' ? undefined : processos.find((p) => p.id === processoSeletivoId)),
    [processos, processoSeletivoId],
  );

  /** Cidades das vagas do processo + municípios que aparecem nas inscrições (ordem alfabética). */
  const opcoesCidade = useMemo(() => {
    const set = new Set<string>();
    for (const v of processoSelecionado?.vagas ?? []) {
      const m = v.municipio?.trim();
      if (m) set.add(m);
    }
    for (const c of candidatos) {
      const m = c.municipio?.trim();
      if (m) set.add(m);
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [processoSelecionado, candidatos]);

  useEffect(() => {
    setActions({
      secondary: {
        label: 'Lista de processos',
        href: Links.ProcessoSeletivo_Index,
        onClick: () => {},
      },
    });
    return () => setActions({});
  }, [setActions]);

  useEffect(() => {
    const q: SearchDto = { skip: 0, take: 2000 };
    Promise.all([
      processoSeletivoService.findAll(q),
      cursoService.findAll({ skip: 0, take: 5000 }),
      instituicaoEnsinoService.Enumerate().catch(() => [] as ReadInstituicaoEnsinoDto[]),
    ])
      .then(([ps, cur, inst]) => {
        setProcessos(ps[0]);
        setCursos(cur[0]);
        setInstituicoesEnsino(inst);
      })
      .catch((e) => toast.error(String(e) || 'Falha ao carregar filtros.'))
      .finally(() => setCarregandoListas(false));
  }, []);

  const recarregarCandidatos = useCallback(async (pid: number) => {
    setCarregandoCandidatos(true);
    try {
      const rows = await carregarTodosCandidatosDoProcesso(pid);
      setCandidatos(rows);
    } catch (e) {
      toast.error(String(e) || 'Falha ao carregar inscrições.');
      setCandidatos([]);
    } finally {
      setCarregandoCandidatos(false);
    }
  }, []);

  useEffect(() => {
    if (processoSeletivoId === '') {
      setCandidatos([]);
      return;
    }
    void recarregarCandidatos(processoSeletivoId);
  }, [processoSeletivoId, recarregarCandidatos]);

  const filtrados = useMemo(() => {
    let list = candidatos;
    if (filtroMunicipio !== '') {
      const alvo = normalizarMunicipio(filtroMunicipio);
      list = list.filter((c) => normalizarMunicipio(c.municipio) === alvo);
    }
    if (filtroCursoId !== '') {
      const alvo = Number(filtroCursoId);
      list = list.filter((c) => Number(c.cursoPretendidoId) === alvo);
    }
    return list;
  }, [candidatos, filtroMunicipio, filtroCursoId]);

  const indicadores = useMemo(() => {
    const total = filtrados.length;
    const rascunhos = filtrados.filter((c) => c.situacao === 'rascunho').length;
    const inscritos = total - rascunhos;
    return { total, rascunhos, inscritos };
  }, [filtrados]);

  const dadosGenero = useMemo(
    () =>
      contarPor(filtrados, (r) =>
        r.genero === 'masculino' ? 'Masculino' : r.genero === 'feminino' ? 'Feminino' : String(r.genero),
      ),
    [filtrados],
  );

  const dadosInstituicao = useMemo(
    () =>
      contarPor(filtrados, (r) => {
        const n = r.instituicaoEnsinoNome?.trim();
        if (n) return n;
        const id = r.instituicaoEnsinoId;
        if (id != null) {
          const razao = razaoSocialPorInstituicaoId.get(id);
          if (razao) return razao;
          return `Instituição #${id}`;
        }
        return 'Não informado';
      }),
    [filtrados, razaoSocialPorInstituicaoId],
  );

  const dadosRaca = useMemo(
    () => contarPor(filtrados, (r) => RACAS_LABELS[r.raca] ?? r.raca),
    [filtrados],
  );

  const dadosPcd = useMemo(
    () =>
      contarPor(filtrados, (r) =>
        r.optaVagasPcd === true ? 'Sim' : r.optaVagasPcd === false ? 'Não' : 'Não informado',
      ),
    [filtrados],
  );

  return (
    <div className="row">
      <div className="col-12">
        <h3>Análises — Processos seletivos</h3>
        <span className="blockquote-footer pb-2 text-end">
          Indicadores e distribuição das inscrições por filtros selecionados.
        </span>
      </div>

      <hr className="pb-3" />

      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Filtros</h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label" htmlFor="filtro-processo">
                  Processo seletivo *
                </label>
                <select
                  id="filtro-processo"
                  className="form-select"
                  disabled={carregandoListas}
                  value={processoSeletivoId === '' ? '' : String(processoSeletivoId)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setProcessoSeletivoId(v === '' ? '' : Number(v));
                    setFiltroMunicipio('');
                    setFiltroCursoId('');
                  }}
                >
                  <option value="">Selecione...</option>
                  {processos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="filtro-cidade">
                  Cidade (município da vaga)
                </label>
                <select
                  id="filtro-cidade"
                  className="form-select"
                  disabled={processoSeletivoId === '' || carregandoListas}
                  value={filtroMunicipio}
                  onChange={(e) => setFiltroMunicipio(e.target.value)}
                >
                  <option value="">Todas</option>
                  {opcoesCidade.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="filtro-curso">
                  Curso pretendido
                </label>
                <select
                  id="filtro-curso"
                  className="form-select"
                  disabled={processoSeletivoId === '' || carregandoListas}
                  value={filtroCursoId === '' ? '' : String(filtroCursoId)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFiltroCursoId(v === '' ? '' : Number(v));
                  }}
                >
                  <option value="">Todos</option>
                  {cursos.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {processoSeletivoId === '' && (
        <div className="col-12">
          <p className="text-muted">Selecione um processo seletivo para carregar os dados e exibir os indicadores.</p>
        </div>
      )}

      {processoSeletivoId !== '' && carregandoCandidatos && (
        <div className="col-12 text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando inscrições...</span>
          </div>
        </div>
      )}

      {processoSeletivoId !== '' && !carregandoCandidatos && (
        <>
          <div className="col-md-4 mb-3">
            <div className="card border-primary h-100">
              <div className="card-body">
                <h6 className="text-muted">Total no recorte</h6>
                <p className="display-6 mb-0">{indicadores.total}</p>
                <small className="text-muted">Registros após filtros de cidade e curso</small>
              </div>
            </div>
          </div>
          <div className="col-md-4 mb-3">
            <div className="card border-success h-100">
              <div className="card-body">
                <h6 className="text-muted">Inscrições efetivadas</h6>
                <p className="display-6 mb-0">{indicadores.inscritos}</p>
                <small className="text-muted">Exclui situação &quot;rascunho&quot;</small>
              </div>
            </div>
          </div>
          <div className="col-md-4 mb-3">
            <div className="card border-warning h-100">
              <div className="card-body">
                <h6 className="text-muted">Rascunhos</h6>
                <p className="display-6 mb-0">{indicadores.rascunhos}</p>
                <small className="text-muted">Inscrições ainda não finalizadas</small>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <PieCard titulo="Distribuição por gênero" data={dadosGenero} />
          </div>
          <div className="col-lg-6 mb-4">
            <PieCard titulo="Opta por vagas reservadas (PCD)" data={dadosPcd} />
          </div>
          <div className="col-lg-6 mb-4">
            <PieCard titulo="Identificação racial" data={dadosRaca} />
          </div>
          <div className="col-lg-6 mb-4">
            <PieCard titulo="Instituição de ensino" data={dadosInstituicao} legendaVertical />
          </div>
        </>
      )}
    </div>
  );
}
